import { useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import type { DiscordTemplate, DiscordWebhook } from '../../types/discord';
import { TRIGGER_LABELS } from '../../types/discord';
import { updateTemplate, deleteTemplate, sendTemplate } from '../../api/discord';
import { EditTemplateModal } from './EditTemplateModal';

interface Props {
  template: DiscordTemplate;
  webhooks: DiscordWebhook[];
  canWrite: boolean;
  onChanged: () => void;
}

function SendModal({
  open,
  template,
  onClose,
}: {
  open: boolean;
  template: DiscordTemplate;
  onClose: () => void;
}) {
  const [vars, setVars] = useState<Record<string, string>>(
    Object.fromEntries(template.variables.map((v) => [v, ''])),
  );
  const [result, setResult] = useState<{ success: boolean; error?: string } | null>(null);
  const [sending, setSending] = useState(false);

  function handleClose() {
    setResult(null);
    onClose();
  }

  async function handleSend() {
    setSending(true);
    setResult(null);
    try {
      const r = await sendTemplate(template.id, vars);
      setResult(r);
    } catch {
      setResult({ success: false, error: 'Request failed' });
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send — {template.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {template.variables.length > 0 ? (
            template.variables.map((v) => (
              <TextField
                key={v}
                label={`{{${v}}}`}
                value={vars[v] ?? ''}
                onChange={(e) => setVars((prev) => ({ ...prev, [v]: e.target.value }))}
                size="small"
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No variables — message will be sent as-is.
            </Typography>
          )}
          {result && (
            <Alert severity={result.success ? 'success' : 'error'}>
              {result.success ? 'Message sent.' : result.error ?? 'Unknown error'}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button variant="contained" onClick={handleSend} disabled={sending}>
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function TemplateCard({ template, webhooks, canWrite, onChanged }: Props) {
  const [sendOpen, setSendOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const webhook = webhooks.find((w) => w.id === template.webhookId);

  async function handleToggle() {
    await updateTemplate(template.id, { active: !template.active });
    onChanged();
  }

  async function handleDelete() {
    if (!confirm(`Delete template "${template.name}"?`)) return;
    await deleteTemplate(template.id);
    onChanged();
  }

  const triggerLabel = TRIGGER_LABELS[template.trigger] ?? template.trigger;

  return (
    <>
      <Card variant="outlined" sx={{ opacity: template.active ? 1 : 0.6 }}>
        <CardContent>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Box flex={1} minWidth={0}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <Typography variant="subtitle1" fontWeight={600} noWrap>
                  {template.name}
                </Typography>
                <Chip
                  label={template.active ? 'Active' : 'Inactive'}
                  size="small"
                  color={template.active ? 'success' : 'default'}
                />
                <Chip label={triggerLabel} size="small" variant="outlined" />
              </Stack>
              {template.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {template.description}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                → {webhook ? `${webhook.name} (${webhook.channel})` : 'Webhook deleted'}
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontFamily: 'monospace', display: 'block', mt: 0.5, whiteSpace: 'pre-wrap' }}
              >
                {template.template}
              </Typography>
              {template.variables.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                  {template.variables.map((v) => (
                    <Chip key={v} label={`{{${v}}}`} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                  ))}
                </Stack>
              )}
              {template.lastSent && (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                  Last sent: {new Date(template.lastSent).toLocaleString()}
                </Typography>
              )}
            </Box>
            {canWrite && (
              <Stack direction="row" spacing={0.5} flexShrink={0}>
                <Tooltip title="Send now">
                  <IconButton size="small" onClick={() => setSendOpen(true)}>
                    <SendIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit template">
                  <IconButton size="small" onClick={() => setEditOpen(true)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={template.active ? 'Disable' : 'Enable'}>
                  <IconButton size="small" onClick={handleToggle}>
                    {template.active ? (
                      <ToggleOnIcon fontSize="small" color="success" />
                    ) : (
                      <ToggleOffIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete template">
                  <IconButton size="small" onClick={handleDelete} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
      <SendModal open={sendOpen} template={template} onClose={() => setSendOpen(false)} />
      <EditTemplateModal
        open={editOpen}
        template={template}
        webhooks={webhooks}
        onClose={() => setEditOpen(false)}
        onSaved={() => { setEditOpen(false); onChanged(); }}
      />
    </>
  );
}
